import {
    faCircleInfo,
    faStar as faStarRegular,
    faTrashCan,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import {faStar} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Popover, Tooltip} from 'antd';
import {CarouselRef} from 'antd/es/carousel';
import {PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    SortableContainer as createSortableContainer,
    SortableElement as createSortableElement,
} from 'react-sortable-hoc';

import {Spinner} from '../../../../../../layout/spinner/spinner';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {
    YandexStoryFileUploaderType,
    YandexStoryFormFieldsEnum,
    YandexStoryFormType,
    YandexStorySlideType,
    YandexStoryTypeEnum,
} from '../../../../../../service/yandex-stories/yandex-stories-type';
import {Form, FormInstance} from '../../../../../../typings/antd';
import {arrayMove} from '../../../../../../util/array';
import {classNames} from '../../../../../../util/css';
import {debounce} from '../../../../../../util/function';
import {Text} from '../../../../../text/text';
import {YandexStoriesCarousel} from '../../../../yandex-stories-preview/carousel/yandex-stories-carousel';
import {YANDEX_STORY__MAX_SLIDES} from '../../../yandex-story-form-helper';
import {YandexStoryFormStep} from '../../step/yandex-story-form-step';

import {YandexStoryActiveSlideControls} from './yandex-story-active-slide-controls';
import {YandexStoryContentStepSlideUpload} from './yandex-story-content-step-slide-upload';
import {
    findInvalidYandexStorySlide,
    isYandexStoryFormFilled,
    validateYandexStorySlideImage,
} from './yandex-story-form-slides-content-step-helper';
import {YandexStoryHowToFindActiveSlideControls} from './yandex-story-how-to-find-active-slide-controls';
import {YandexStorySettingsPanel} from './yandex-story-settings-panel';
import {useYandexStorySlideUploadLock} from './yandex-story-slide-upload-hook';
import {YandexStorySlidesUploadPanel} from './yandex-story-slides-upload-panel';
import * as styles from './yandex-story-form-slides-content-step.scss';

type SortableContainerPropsType = PropsWithChildren<{
    className?: string;
}>;

const SortableContainer = createSortableContainer<SortableContainerPropsType>(
    (sortableContainerProps: SortableContainerPropsType) => {
        const {children, className} = sortableContainerProps;

        return <div className={className}>{children}</div>;
    }
);

type SortableItemPropsType = PropsWithChildren<{
    className?: string;
}>;

const SortableItem = createSortableElement<SortableItemPropsType>((sortableItemProps: SortableItemPropsType) => {
    const {children, className} = sortableItemProps;

    return <div className={className}>{children}</div>;
});

type PropsType = {
    formInstance: FormInstance<YandexStoryFormType>;
    formMode: 'create' | 'edit';
    onSubmit: (values: YandexStoryFormType) => Promise<void>;
    onPreviousStep: VoidFunction;
    isFirstStep: boolean;
};

// The slides rail deserves a component of its own; until it gets one, this step stays over both limits.
// eslint-disable-next-line complexity, max-statements
export function YandexStoryFormSlidesContentStep(props: PropsType): JSX.Element {
    const {formInstance, formMode, onSubmit, onPreviousStep, isFirstStep} = props;

    const {getLocalizedString} = useLocale();

    const slides = Form.useWatch([YandexStoryFormFieldsEnum.Slides], {
        form: formInstance,
        preserve: true,
    });

    const storyType = Form.useWatch([YandexStoryFormFieldsEnum.Type], {
        form: formInstance,
        preserve: true,
    });

    const cover = Form.useWatch([YandexStoryFormFieldsEnum.Cover], {
        form: formInstance,
        preserve: true,
    });

    const publicationRange = Form.useWatch([YandexStoryFormFieldsEnum.PublicationRange], {
        form: formInstance,
        preserve: true,
    });

    const storyName = Form.useWatch([YandexStoryFormFieldsEnum.Name], {
        form: formInstance,
        preserve: true,
    });

    const [activeSlideOrder, setActiveSlideOrder] = useState<number>(1);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const {isPanelUploading, isRailUploading, onPanelUploadingChange, onRailUploadingChange} =
        useYandexStorySlideUploadLock();

    const carouselRef = useRef<CarouselRef | null>(null);

    useEffect(() => {
        carouselRef.current?.goTo(activeSlideOrder - 1);
    }, [activeSlideOrder, slides]);

    const {
        slidesByOrder,
        activeSlideKey,
        orderedSlideEntries,
        hasSlides,
        hasUnreadySlides,
        activeSlide,
        coverSlideId,
        isActiveSlideCover,
        slidesCount,
    } = useMemo(() => {
        const sortedEntries: Array<[string, YandexStorySlideType]> = slides
            ? Object.entries(slides)
                  .sort(([_key1, value1], [_key2, value2]) => value1.order - value2.order)
                  .map(([key, value], index) => {
                      return [
                          key,
                          {
                              ...value,
                              order: index + 1,
                          },
                      ];
                  })
            : [];

        const orderedSlides = sortedEntries.map(([_key, value]) => value);
        const currentSlide = orderedSlides[activeSlideOrder - 1] || null;
        // With no explicit pick the first slide is the cover, as the create request builds it.
        const currentCoverSlideId = cover?.id || orderedSlides[0]?.id;

        return {
            slidesByOrder: orderedSlides,
            activeSlideKey:
                sortedEntries.map(([key]) => {
                    return key;
                })?.[activeSlideOrder - 1] || null,
            orderedSlideEntries: sortedEntries,
            hasSlides: orderedSlides.length > 0,
            slidesCount: orderedSlides.length,
            hasUnreadySlides: orderedSlides.some((slide) => slide.status !== 'done'),
            activeSlide: currentSlide,
            coverSlideId: currentCoverSlideId,
            isActiveSlideCover: Boolean(currentSlide && currentSlide.id === currentCoverSlideId),
        };
    }, [activeSlideOrder, cover?.id, slides]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onCarouselActiveIndexChange = useCallback(
        debounce((_currentSlideIndex: number, nextSlideIndex: number) => {
            setActiveSlideOrder(nextSlideIndex + 1);
        }, 200),
        []
    );

    const handleSortEnd = useCallback(
        ({oldIndex, newIndex}: {oldIndex: number; newIndex: number}) => {
            setActiveSlideOrder(newIndex + 1);

            if (oldIndex === newIndex) {
                return;
            }

            const newStories = arrayMove([...slidesByOrder], oldIndex, newIndex);

            formInstance.setFieldValue(
                [YandexStoryFormFieldsEnum.Slides],
                Object.fromEntries(newStories.map((item, index) => [item.uid, {...item, order: index + 1}]))
            );
        },
        [formInstance, slidesByOrder]
    );

    const handleSelectAsCover = useCallback(() => {
        if (!activeSlide) {
            return;
        }

        formInstance.setFieldValue([YandexStoryFormFieldsEnum.Cover], {id: activeSlide.id, url: activeSlide.url});
    }, [activeSlide, formInstance]);

    const handleRemoveActiveSlide = useCallback(() => {
        const newSlidesValue: YandexStoryFileUploaderType = orderedSlideEntries.reduce(
            (accumulator, [key, slideValue]) => {
                if (!slideValue) {
                    return accumulator;
                }

                if (slideValue.order === activeSlideOrder) {
                    return accumulator;
                }

                if (slideValue.order > activeSlideOrder) {
                    return {
                        ...accumulator,
                        [key]: {
                            ...slideValue,
                            order: slideValue.order - 1,
                        },
                    };
                }

                return {
                    ...accumulator,
                    [key]: slideValue,
                };
            },
            {}
        );

        // Dropping the cover slide falls the cover back to the first remaining one.
        if (activeSlide && activeSlide.id === cover?.id) {
            formInstance.setFieldValue([YandexStoryFormFieldsEnum.Cover], null);
        }

        formInstance.setFieldValue([YandexStoryFormFieldsEnum.Slides], newSlidesValue);

        if (activeSlideOrder > Object.keys(newSlidesValue).length) {
            const newActiveOrder = activeSlideOrder - 1;

            setActiveSlideOrder(newActiveOrder);
        }
    }, [activeSlide, activeSlideOrder, cover?.id, formInstance, orderedSlideEntries]);

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);

        try {
            await formInstance.validateFields();

            const invalidSlide = findInvalidYandexStorySlide(slidesByOrder);

            if (invalidSlide) {
                setActiveSlideOrder(invalidSlide.order);
                await formInstance.validateFields();

                throw new Error('validation error');
            }

            await onSubmit(formInstance.getFieldsValue(true));
        } catch {
            const slideWithErrorKeys = formInstance
                .getFieldsError()
                .filter(
                    (fieldError) =>
                        fieldError.errors.length > 0 &&
                        fieldError.name.includes(YandexStoryFormFieldsEnum.Slides) &&
                        fieldError.name[1]
                )
                .map((fieldError) => fieldError.name[1]);

            const slidesWithErrorByOrder = slideWithErrorKeys
                .map((slideKey) => {
                    return slides?.[String(slideKey)];
                })
                .filter(Boolean)
                .sort((slide1, slide2) => slide1.order - slide2.order);

            if (slidesWithErrorByOrder[0]) {
                setActiveSlideOrder(slidesWithErrorByOrder[0].order);
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [formInstance, onSubmit, slides, slidesByOrder]);

    // Both halves of the guard behind the submit button: this one blocks it, `handleSubmit` reports
    // what is wrong once the fields it covers are filled in.
    const isFormFilled = isYandexStoryFormFilled(storyName, slidesByOrder);

    // A publish date turns the create action into scheduling.
    const submitLangKey = publicationRange?.[0] ? 'BUTTON__SCHEDULE' : 'BUTTON__PUBLISH';

    return (
        <YandexStoryFormStep
            footer={
                <Text
                    className={styles.YandexStoryFormSlidesContentStep_moderationHint}
                    secondary
                    small
                    stringKey="YANDEX_STORY_FORM__MODERATION_HINT"
                />
            }
            leftFooterButtonConfig={{
                langKeyType: isFirstStep ? 'POPUP__BUTTON__CANCEL' : 'BUTTON__BACK',
                onClick: onPreviousStep,
            }}
            rightFooterButtonConfig={{
                disabled: isSubmitting || !hasSlides || !isFormFilled,
                langKeyType: formMode === 'create' ? submitLangKey : 'BUTTON__UPDATE',
                loading: isSubmitting,
                onClick: handleSubmit,
            }}
            // A slide that is still uploading (or failed) already keeps the button disabled through
            // `isFormFilled`; it is the only such reason the user cannot see, so it gets the tooltip.
            rightFooterButtonTooltip={
                hasUnreadySlides ? <Locale stringKey="YANDEX_STORY_FORM__SLIDES_CONTENT__SLIDES_NOT_READY" /> : ''
            }
        >
            <div className={styles.YandexStoryFormSlidesContentStep}>
                <div className={styles.YandexStoryFormSlidesContentStep_contentSettingsWrapper}>
                    {hasSlides ? (
                        <div className={styles.YandexStoryFormSlidesContentStep_carouselWrapper}>
                            <YandexStoriesCarousel
                                afterChange={onCarouselActiveIndexChange}
                                carouselRef={carouselRef}
                                key="yandex-stories-carousel"
                                slides={
                                    slidesByOrder.map((item) => {
                                        return {
                                            image: {
                                                url: item.url,
                                            },
                                            buttonConfig: item.buttonConfig,
                                        };
                                    }) || []
                                }
                            />

                            {activeSlide && (
                                <div className={styles.YandexStoryFormSlidesContentStep_slideActions}>
                                    <Button
                                        className={styles.YandexStoryFormSlidesContentStep_coverButton}
                                        disabled={isActiveSlideCover}
                                        icon={<FontAwesomeIcon icon={isActiveSlideCover ? faStar : faStarRegular} />}
                                        onClick={handleSelectAsCover}
                                    >
                                        <Locale
                                            stringKey={
                                                isActiveSlideCover
                                                    ? 'YANDEX_STORY_FORM__COVER__CURRENT'
                                                    : 'YANDEX_STORY_FORM__COVER__MAKE'
                                            }
                                        />
                                    </Button>

                                    <Button
                                        danger
                                        icon={<FontAwesomeIcon icon={faTrashCan} />}
                                        onClick={handleRemoveActiveSlide}
                                        type="primary"
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <YandexStorySlidesUploadPanel
                            formInstance={formInstance}
                            isUploadBlocked={isRailUploading}
                            onUploadingChange={onPanelUploadingChange}
                        />
                    )}

                    <YandexStorySettingsPanel formInstance={formInstance} hasSlides={hasSlides} storyType={storyType}>
                        {storyType === YandexStoryTypeEnum.Story && activeSlideKey && (
                            <YandexStoryActiveSlideControls
                                activeSlideKey={activeSlideKey}
                                formInstance={formInstance}
                            />
                        )}

                        {storyType === YandexStoryTypeEnum.Navigation && activeSlideKey && (
                            <YandexStoryHowToFindActiveSlideControls
                                activeSlideKey={activeSlideKey}
                                formInstance={formInstance}
                                onDeleteSlide={handleRemoveActiveSlide}
                            />
                        )}
                    </YandexStorySettingsPanel>
                </div>

                <div className={styles.YandexStoryFormSlidesContentStep_slidesRailContainer}>
                    <div className={styles.YandexStoryFormSlidesContentStep_railHint}>
                        <Text block lighter small stringKey="YANDEX_STORY_FORM__SLIDES_RAIL__DRAG_HINT" />

                        <Popover
                            content={
                                <ul className={styles.YandexStoryFormSlidesContentStep_popoverContent}>
                                    <li>
                                        <Locale stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_1__SHORT" />
                                    </li>
                                    <li>
                                        <Locale stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_2__SHORT" />
                                    </li>
                                    <li>
                                        <Locale stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_3__SHORT" />
                                    </li>
                                    <li>
                                        <Locale stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_4__SHORT" />
                                    </li>
                                </ul>
                            }
                        >
                            <Text block lightest>
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </Text>
                        </Popover>
                    </div>

                    <SortableContainer
                        axis="xy"
                        className={styles.YandexStoryFormSlidesContentStep_slidesRail}
                        helperClass={styles.YandexStoryFormSlidesContentStep_slide__dragging}
                        onSortEnd={handleSortEnd}
                    >
                        {slidesByOrder.map((item, index) => {
                            return (
                                <SortableItem index={index} key={item.order}>
                                    <div
                                        className={classNames(styles.YandexStoryFormSlidesContentStep_slide, {
                                            [styles.YandexStoryFormSlidesContentStep_slide__active]:
                                                item.order === activeSlideOrder,
                                        })}
                                        style={{
                                            backgroundImage: `url("${item.url}")`,
                                        }}
                                    >
                                        {item.status === 'uploading' && <Spinner />}

                                        {item.status === 'error' && <FontAwesomeIcon icon={faTriangleExclamation} />}

                                        {item.id === coverSlideId && (
                                            <Tooltip title={<Locale stringKey="YANDEX_STORY_FORM__COVER__CURRENT" />}>
                                                <span
                                                    className={styles.YandexStoryFormSlidesContentStep_slideCoverBadge}
                                                >
                                                    <FontAwesomeIcon icon={faStar} />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </SortableItem>
                            );
                        })}

                        {slidesCount < YANDEX_STORY__MAX_SLIDES && (
                            <YandexStoryContentStepSlideUpload
                                formInstance={formInstance}
                                isUploadBlocked={isPanelUploading}
                                onUploadingChange={onRailUploadingChange}
                                slidesCount={slidesCount}
                            />
                        )}
                    </SortableContainer>

                    <div className={styles.YandexStoryFormSlidesContentStep_counterWrapper}>
                        <Text block lighter>
                            {`${slidesCount} / ${YANDEX_STORY__MAX_SLIDES}`}
                        </Text>
                    </div>
                </div>

                {Object.keys(slides || {}).map((key) => (
                    <Form.Item
                        hidden
                        key={key}
                        name={[YandexStoryFormFieldsEnum.Slides, key]}
                        rules={[
                            validateYandexStorySlideImage(
                                getLocalizedString('YANDEX_STORY_FORM__SLIDES_CONTENT__SLIDES_NOT_READY')
                            ),
                        ]}
                    />
                ))}
            </div>
        </YandexStoryFormStep>
    );
}
